import axios from 'axios';
import i18n from '../i18n';

// 根据环境自动选择API基础URL
const getApiBaseUrl = () => {
  // 在生产环境中，使用相对路径让Vercel自动路由到后端
  if (process.env.NODE_ENV === 'production') {
    return '';  // 相对路径，由Vercel路由处理
  }
  // 开发环境使用本地后端
  return process.env.REACT_APP_API_URL || 'http://localhost:8800';
};

const API_BASE_URL = getApiBaseUrl();

export interface StoryBranch {
  id: string;
  title: string;
  description: string;
  status?: 'active' | 'completed' | 'abandoned';
  created_at?: string;
  weight?: number; // 可选：用于表示情节的重要性或影响力
}

export interface StoryNode {
  id: string;
  label: string;
  title?: string;
  color?: string;
  shape?: string;
  content?: string;
  created_at?: string;
  tags?: string[];
}

export interface StoryEdge {
  id?: string;
  from: string;
  to: string;
  arrows?: string;
  color?: string;
  label?: string;
  type?: string;
}

export interface StoryResponse {
  nodes: StoryNode[];
  edges: StoryEdge[];
  branches: StoryBranch[];
}

export interface InteractiveStoryResponse {
  node: StoryNode;
  directions?: string[];
}

export interface StoryStats {
  total_nodes: number;
  total_branches: number;
  completed_branches: number;
  active_branches: number;
  abandoned_branches: number;
}

export interface TokenResponse {
  success: boolean;
  message: string;
  token: string | null;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 简单的认证服务
const simpleAuthService = {
  getAuthToken: () => {
    // 从localStorage获取token，如果没有则返回null
    return localStorage.getItem('auth_token');
  },
  
  setAuthToken: (token: string) => {
    localStorage.setItem('auth_token', token);
  },
  
  clearAuthToken: () => {
    localStorage.removeItem('auth_token');
  }
};

// 请求拦截器，自动添加认证token
api.interceptors.request.use(async (config) => {
  // Skip auth for public endpoints
  const publicEndpoints = ['/api/config/global', '/api/auth/token'];
  const isPublicEndpoint = publicEndpoints.some(endpoint => config.url?.includes(endpoint));
  
  if (!isPublicEndpoint) {
    try {
      const token = simpleAuthService.getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('No authentication token available for request:', config.url);
      }
    } catch (error) {
      console.error('Failed to get authentication token:', error);
    }
  }
  
  return config;
});

// 响应拦截器，处理认证错误和自动刷新token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Token无效或过期，尝试获取新token
        console.warn('Authentication failed, attempting to refresh token');
        simpleAuthService.clearAuthToken();
        
        // 获取新token
        const tokenResponse = await api.get('/api/auth/token');
        if (tokenResponse.data.success && tokenResponse.data.token) {
          const newToken = tokenResponse.data.token;
          simpleAuthService.setAuthToken(newToken);
          
          // 更新原始请求的Authorization头
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          
          // 重试原始请求
          console.log('Token refreshed successfully, retrying original request');
          return api(originalRequest);
        } else {
          console.error('Failed to refresh token:', tokenResponse.data.message);
        }
      } catch (refreshError) {
        console.error('Error during token refresh:', refreshError);
        simpleAuthService.clearAuthToken();
      }
    }
    
    return Promise.reject(error);
   }
 );

export const storyApi = {
  // 认证相关接口
  getToken: async (): Promise<TokenResponse> => {
    const response = await api.get('/api/auth/token');
    return response.data;
  },

  getUserProfile: async (): Promise<any> => {
    const response = await api.get('/api/user/profile');
    return response.data;
  },

  getGlobalConfig: async (): Promise<any> => {
    const response = await api.get('/api/config/global');
    return response.data;
  },

  // 生成初始剧情分支
  generateInitialBranches: async (storySetting: string, genre?: string, tone?: string, branchCount?: number): Promise<StoryResponse> => {
    const data = {
      story_setting: storySetting,
      genre,
      tone,
      branch_count: branchCount || 3,
      language: i18n.language
    };
    const response = await api.post('/api/story/generate', data);
    return response.data;
  },

  // 生成下一个分支
  generateNextBranches: async (nodeId: string, context?: string, storyType?: string, storyStyle?: string, branchCount?: number): Promise<StoryResponse> => {
    const data = {
      node_id: nodeId,
      context,
      story_type: storyType,
      story_style: storyStyle,
      branch_count: branchCount || 2,
      language: i18n.language
    };
    const response = await api.post('/api/story/expand', data);
    return response.data;
  },
  
  // 生成互动故事节点
  generateInteractiveStory: async (storySetting: string, storyType?: string, storyStyle?: string, direction?: string, nodeId?: string): Promise<StoryResponse> => {
    // 根据是否有方向决定使用哪个API端点和数据结构
    let data: any;
    let endpoint: string;
    
    if (direction && nodeId) {
      // 扩展现有故事
      endpoint = '/api/story/expand';
      data = {
        node_id: nodeId,
        context: storySetting, // 在这里，storySetting实际上是故事上下文
        story_type: storyType,
        story_style: storyStyle,
        direction,
        branch_count: 1,
        language: i18n.language,
        interactive: true
      };
    } else {
      // 生成新故事
      endpoint = '/api/story/generate';
      data = {
        story_setting: storySetting,
        genre: storyType,
        tone: storyStyle,
        branch_count: 1,
        language: i18n.language,
        interactive: true
      };
    }
    
    const response = await api.post(endpoint, data);
    return response.data;
  },

  // 生成故事（兼容性）
  generateStory: async (data: {
    story_setting: string;
    genre?: string;
    tone?: string;
    character_count?: number;
  }): Promise<StoryResponse> => {
    const requestData = {
      ...data,
      language: i18n.language
    };
    const response = await api.post('/api/story/generate', requestData);
    return response.data;
  },

  // 扩展分支（兼容性）
  expandStory: async (data: {
    node_id: string;
    context?: string;
    direction?: string;
    branch_count?: number;
  }): Promise<StoryResponse> => {
    const requestData = {
      ...data,
      language: i18n.language
    };
    const response = await api.post('/api/story/expand', requestData);
    return response.data;
  },

  // 获取故事历史
  getStoryHistory: async (): Promise<StoryResponse> => {
    const response = await api.get('/api/story/history');
    return response.data;
  },

  // 更新节点
  updateNode: async (node_id: string, data: Partial<StoryNode>): Promise<any> => {
    const response = await api.put(`/api/story/node/${node_id}`, data);
    return response.data;
  },

  // 更新分支
  updateBranch: async (branch_id: string, data: Partial<StoryBranch>): Promise<any> => {
    const response = await api.put(`/api/story/branch/${branch_id}`, data);
    return response.data;
  },

  // 删除节点
  deleteNode: async (node_id: string): Promise<any> => {
    const response = await api.delete(`/api/story/node/${node_id}`);
    return response.data;
  },

  // 搜索故事内容
  searchStory: async (data: {
    query: string;
    search_type?: 'title' | 'content' | 'tags' | 'all';
    limit?: number;
  }): Promise<{ results: StoryNode[]; count: number }> => {
    const response = await api.post('/api/story/search', data);
    return response.data;
  },

  // 获取可用类型
  getGenres: async (): Promise<{ genres: string[] }> => {
    const response = await api.get('/api/story/genres');
    return response.data;
  },

  // 获取可用语调
  getTones: async (): Promise<{ tones: string[] }> => {
    const response = await api.get('/api/story/tones');
    return response.data;
  },

  // 清空故事历史
  clearStory: async (): Promise<any> => {
    const response = await api.post('/api/story/clear');
    return response.data;
  },

  // 获取特定节点
  getNode: async (node_id: string): Promise<{ node: StoryNode }> => {
    const response = await api.get(`/api/story/node/${node_id}`);
    return response.data;
  },

  // 获取特定分支
  getBranch: async (branch_id: string): Promise<{ branch: StoryBranch }> => {
    const response = await api.get(`/api/story/branch/${branch_id}`);
    return response.data;
  },

  // 获取世界观设定AI建议
  getWorldbuildingSuggestion: async (data: {
    element_type: string;
    element_name: string;
    field: string;
    existing_content?: string;
  }): Promise<{ suggestion: string }> => {
    const requestData = {
      ...data,
      language: i18n.language
    };
    const response = await api.post('/api/ai/worldbuilding/suggest', requestData);
    return response.data;
  },

  // 获取角色AI建议
  getCharacterSuggestion: async (data: {
    element_name: string;
    field: string;
    existing_content?: string;
  }): Promise<{ suggestion: string }> => {
    const requestData = {
      element_type: 'character', // 固定为character类型
      ...data,
      language: i18n.language
    };
    const response = await api.post('/api/ai/character/suggest', requestData);
    return response.data;
  },

  // 生成故事事件
  generateStoryEvent: async (data: {
    characters: any[];
    scenes: any[];
    worldMetadata?: any;
    existingEvents?: any[];
  }): Promise<{
    title: string;
    content: string;
    narrative: string;
    dialogue: Array<{ character: string; content: string; type?: string }>;
    summary: string;
  }> => {
    const requestData = {
      ...data,
      language: i18n.language
    };
    const response = await api.post('/api/ai/story/event', requestData);
    return response.data;
  },
};

// 导出认证服务，供其他组件使用
export { simpleAuthService };

export default api;